/**
 * Shows why a taxonomy draft could not be saved.
 *
 * A modal rather than a toast because the backend reports every problem at once
 * — a single mistake repeated across a dimension can be 25 entries — and the
 * author needs the list to stay on screen while they correct the JSON behind it.
 *
 * Issues sharing a rule and a remediation are grouped, so "25 label IDs contain
 * an illegal character" reads as one problem with 25 locations rather than 25
 * unrelated failures.
 */
import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../../../ui/dialog';
import { Button } from '../../../../ui/button';
import { ScrollArea } from '../../../../ui/scroll-area';
import type {
  StructuredValidationDetail,
  StructuredValidationIssue,
} from '../../../../../services/types/errors';

interface TaxonomyValidationErrorModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  detail: StructuredValidationDetail;
}

interface IssueGroup {
  rule: string;
  hint?: string;
  issues: StructuredValidationIssue[];
}

/**
 * Group issues that share both a rule and a remediation, preserving first-seen order.
 *
 * Grouping on the rule alone would be wrong: a rule is a JSON Schema keyword, and
 * two unrelated constraints can trip the same one. An invalid `version` and a
 * colon in a label ID both report `schema.pattern`, but they are different
 * problems with different fixes — merging them would show one under the other's
 * remediation. The hint is what distinguishes them, so it belongs in the key.
 */
const groupIssues = (issues: StructuredValidationIssue[]): IssueGroup[] => {
  const groups: IssueGroup[] = [];
  const byKey = new Map<string, IssueGroup>();

  issues.forEach((issue) => {
    // JSON-encoded pair rather than a delimited string: no separator can
    // collide with a rule name or appear inside a hint.
    const key = JSON.stringify([issue.rule, issue.hint ?? '']);
    let group = byKey.get(key);
    if (!group) {
      group = { rule: issue.rule, hint: issue.hint, issues: [] };
      byKey.set(key, group);
      groups.push(group);
    }
    group.issues.push(issue);
  });

  return groups;
};

const IssueList: React.FC<{ groups: IssueGroup[]; testIdPrefix: string }> = ({
  groups,
  testIdPrefix,
}) => (
  <div className="space-y-3">
    {groups.map((group, groupIndex) => (
      <div
        key={`${group.rule}-${groupIndex}`}
        className="rounded-md border p-3"
        // Indexed rather than keyed on the rule: two groups can share a rule
        // when they carry different remediations, and testids must stay unique.
        data-testid={`${testIdPrefix}-group-${groupIndex}`}
        data-rule={group.rule}
      >
        <div className="flex items-baseline justify-between gap-2">
          <p className="text-sm font-medium">{group.issues[0].message}</p>
          {group.issues.length > 1 && (
            <span className="shrink-0 text-xs text-muted-foreground">
              {group.issues.length} occurrences
            </span>
          )}
        </div>

        {group.hint && (
          <p
            className="mt-1.5 text-xs text-muted-foreground"
            data-testid={`${testIdPrefix}-hint-${groupIndex}`}
          >
            {group.hint}
          </p>
        )}

        <ul className="mt-2 space-y-1">
          {group.issues.map((issue, index) => (
            <li
              key={`${issue.path}-${index}`}
              className="font-mono text-xs text-muted-foreground"
              data-testid={`${testIdPrefix}-row`}
            >
              {issue.path}
              {group.issues.length > 1 && issue.message !== group.issues[0].message && (
                <span className="ml-2 font-sans">{issue.message}</span>
              )}
            </li>
          ))}
        </ul>
      </div>
    ))}
  </div>
);

const TaxonomyValidationErrorModal: React.FC<TaxonomyValidationErrorModalProps> = ({
  open,
  onOpenChange,
  detail,
}) => {
  const [showWarnings, setShowWarnings] = useState(false);

  const errorGroups = groupIssues(detail.errors);
  const warnings = detail.warnings ?? [];
  const warningGroups = groupIssues(warnings);
  const problemCount = detail.errors.length;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="sm:max-w-[700px] max-h-[80vh]"
        data-testid="taxonomy-validation-error-modal"
      >
        <DialogHeader>
          <DialogTitle>
            Cannot save — {problemCount} {problemCount === 1 ? 'problem' : 'problems'} found
          </DialogTitle>
          <DialogDescription>
            Your changes have not been saved. Fix the problems below in the JSON,
            then save again.
          </DialogDescription>
        </DialogHeader>

        <ScrollArea className="h-[50vh] pr-3">
          <IssueList groups={errorGroups} testIdPrefix="validation-error" />

          {warnings.length > 0 && (
            <div className="mt-4">
              <button
                type="button"
                className="text-xs text-muted-foreground underline"
                onClick={() => setShowWarnings((shown) => !shown)}
                data-testid="validation-warnings-toggle"
              >
                {showWarnings ? 'Hide' : 'Show'} {warnings.length}{' '}
                {warnings.length === 1 ? 'warning' : 'warnings'} (these do not block saving)
              </button>
              {showWarnings && (
                <div className="mt-2">
                  <IssueList groups={warningGroups} testIdPrefix="validation-warning" />
                </div>
              )}
            </div>
          )}
        </ScrollArea>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            data-testid="validation-modal-close"
          >
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default TaxonomyValidationErrorModal;
